(function () {
  /** @param {HTMLElement} root */
  function initTabs(root) {
    const tabs = root.querySelectorAll('[data-product-tab]');
    const panels = root.querySelectorAll('[data-product-tabpanel]');
    if (!tabs.length || !panels.length) return;

    /** @param {string} id */
    function activate(id) {
      tabs.forEach((tab) => {
        if (!(tab instanceof HTMLButtonElement)) return;
        const isActive = tab.dataset.productTab === id;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      panels.forEach((panel) => {
        if (!(panel instanceof HTMLElement)) return;
        const isActive = panel.dataset.productTabpanel === id;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    }

    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLButtonElement)) return;
      tab.addEventListener('click', () => {
        const id = tab.dataset.productTab;
        if (id) activate(id);
      });
    });
  }

  /**
   * @param {HTMLElement} box
   * @param {HTMLElement} textEl
   * @param {HTMLButtonElement | null} more
   * @param {string} clampClass
   * @param {boolean} ellipsis
   * @param {boolean} expandOnClick
   */
  function bindClampedText(box, textEl, more, clampClass, ellipsis, expandOnClick) {
    const fullText = textEl.textContent?.trim() || '';
    const words = fullText.split(/\s+/).filter(Boolean);
    let resizeFrame = 0;
    let expanded = false;

    function fits() {
      return box.scrollHeight <= box.clientHeight + 1;
    }

    function fit() {
      if (expanded || !words.length || box.offsetParent === null) return;

      box.classList.add(clampClass);
      if (more) more.hidden = true;
      textEl.textContent = fullText;

      if (fits()) {
        box.classList.remove(clampClass);
        return;
      }

      if (more) more.hidden = false;

      let lowerBound = 1;
      let upperBound = words.length;
      let fittingWordCount = 1;

      while (lowerBound <= upperBound) {
        const wordCount = Math.floor((lowerBound + upperBound) / 2);
        const slice = words.slice(0, wordCount).join(' ');
        textEl.textContent = ellipsis ? `${slice}…` : slice;

        if (fits()) {
          fittingWordCount = wordCount;
          lowerBound = wordCount + 1;
        } else {
          upperBound = wordCount - 1;
        }
      }

      const slice = words.slice(0, fittingWordCount).join(' ');
      textEl.textContent = ellipsis ? `${slice}…` : slice;
    }

    if (more && expandOnClick) {
      more.addEventListener('click', () => {
        expanded = true;
        more.hidden = true;
        textEl.textContent = fullText;
        box.classList.remove(clampClass);
      });
    }

    requestAnimationFrame(fit);
    document.fonts?.ready.then(fit);
    window.addEventListener('resize', () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(fit);
    });
  }

  /** @param {HTMLElement} root */
  function initSuitsLine(root) {
    const line = root.querySelector('[data-product-suits]');
    const textEl = line?.querySelector('[data-product-suits-text]');
    const more = line?.querySelector('[data-product-suits-more]');
    if (!(line instanceof HTMLElement) || !(textEl instanceof HTMLElement)) return;
    if (!(more instanceof HTMLButtonElement)) return;

    let resizeFrame = 0;

    function fit() {
      if (line.classList.contains('is-expanded')) return;
      more.hidden = true;
      const overflows = textEl.scrollWidth > textEl.clientWidth + 1;
      more.hidden = !overflows;
    }

    more.addEventListener('click', () => {
      line.classList.add('is-expanded');
      more.hidden = true;
    });

    requestAnimationFrame(fit);
    document.fonts?.ready.then(fit);
    window.addEventListener('resize', () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(fit);
    });
  }

  /** @param {HTMLElement} root */
  function initDescription(root) {
    const excerpt = root.querySelector('[data-product-description-excerpt]');
    const textEl = root.querySelector('[data-product-description-excerpt-text]');
    const textBlock = root.querySelector('[data-product-description-text]');
    const full = root.querySelector('[data-product-description-full]');
    const more = root.querySelector('[data-product-description-more]');
    const less = root.querySelector('[data-product-description-less]');
    if (!(excerpt instanceof HTMLElement) || !(textEl instanceof HTMLElement) || !(textBlock instanceof HTMLElement)) {
      return;
    }

    bindClampedText(
      excerpt,
      textEl,
      more instanceof HTMLButtonElement ? more : null,
      'product-page__description-excerpt--truncated',
      false,
      false,
    );

    if (more instanceof HTMLButtonElement && full instanceof HTMLElement) {
      more.addEventListener('click', () => {
        textBlock.hidden = true;
        full.hidden = false;
      });
    }

    if (less instanceof HTMLButtonElement && full instanceof HTMLElement) {
      less.addEventListener('click', () => {
        full.hidden = true;
        textBlock.hidden = false;
      });
    }
  }

  /** @param {string} title */
  function formatVariantLabel(title) {
    const value = title.trim();
    if (!value || /^default title$/i.test(value)) return value;
    if (/side$/i.test(value)) return value;

    if (/driver|passenger|left|right/i.test(value)) {
      const cleaned = value.replace(/\s+Side$/i, '').trim();
      if (/driver/i.test(cleaned) && /passenger/i.test(cleaned)) {
        return `${cleaned.replace(/\s*\+\s*/g, ' + ')} side`;
      }
      return `${cleaned} side`;
    }

    return value;
  }

  /** @param {HTMLElement} root */
  function readVariantRecords(root) {
    const node = root.querySelector('[data-product-variants]');
    if (!(node instanceof HTMLScriptElement) && !(node instanceof HTMLElement)) return [];
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} template
   * @param {string} token
   * @param {string} value
   */
  function fillTemplate(template, token, value) {
    return template
      .replace(new RegExp(`\\{\\{\\s*${token}\\s*\\}\\}`, 'gi'), value)
      .replaceAll('__AMOUNT__', value)
      .replaceAll('__SKU__', value);
  }

  /**
   * @param {HTMLElement} root
   * @param {{
   *   id: number | string;
   *   title?: string;
   *   available?: boolean;
   *   price?: string;
   *   inventory?: number;
   *   tracked?: boolean;
   *   mediaId?: string | number;
   *   sku?: string;
   * }} variant
   */
  function applyVariantState(root, variant) {
    const form = root.querySelector('.product-page__purchase-form');
    if (form instanceof HTMLFormElement) {
      form.dataset.variantAvailable = variant.available ? 'true' : 'false';
    }

    const price = root.querySelector('[data-product-price]');
    if (price instanceof HTMLElement && variant.price) {
      price.textContent = variant.price;
    }

    const threshold = Number(root.dataset.productLowStockThreshold || 5);
    const inventory = Number(variant.inventory);
    const showLowStock =
      Boolean(variant.tracked) &&
      Boolean(variant.available) &&
      Number.isFinite(inventory) &&
      inventory > 0 &&
      inventory <= threshold;
    const stock = root.querySelector('[data-product-stock]');
    const stockQty = root.querySelector('[data-product-stock-qty]');
    if (stock instanceof HTMLElement) {
      stock.hidden = !showLowStock;
      if (stockQty instanceof HTMLElement && Number.isFinite(inventory)) {
        stockQty.textContent = String(inventory);
      }
    }

    const skuLabel = root.querySelector('[data-product-sku-label]');
    const partNumbers = root.querySelector('[data-product-part-numbers]');
    if (!root.dataset.productApgPart && skuLabel instanceof HTMLElement) {
      if (variant.sku) {
        const template = root.dataset.productSkuTemplate || 'Part # {{ number }}';
        skuLabel.textContent = fillTemplate(template, 'number', variant.sku);
        skuLabel.hidden = false;
      } else {
        skuLabel.textContent = '';
        skuLabel.hidden = true;
      }
    }
    if (partNumbers instanceof HTMLElement && !root.dataset.productApgPart) {
      partNumbers.hidden = !variant.sku;
    }

    const soldOut = !variant.available;
    const soldOutLabel = root.dataset.productSoldOut || 'Sold out';
    root
      .querySelectorAll('[data-product-purchase] [data-product-card-add], [data-product-sticky-add]')
      .forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        button.disabled = soldOut;
        const labelNode = button.querySelector('.product-card__add-label');
        if (labelNode) {
          labelNode.textContent = soldOut ? soldOutLabel : button.dataset.labelDefault || 'Add to cart';
        }
      });
    root
      .querySelectorAll('[data-product-purchase] .product-page__cart, [data-product-sticky-cart] .product-card__cart')
      .forEach((cart) => {
        cart.classList.toggle('product-page__cart--sold-out', soldOut);
        cart.classList.toggle('product-card__cart--disabled', soldOut);
      });

    const wallet = root.querySelector('[data-product-wallet]');
    if (wallet instanceof HTMLElement) wallet.hidden = soldOut;

    root.querySelector('[data-product-media]')?.dispatchEvent(
      new CustomEvent('map:product-show-media', {
        bubbles: true,
        detail: { mediaId: variant.mediaId ? String(variant.mediaId) : '' },
      }),
    );

    if (variant.id && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    }
  }

  /** @param {HTMLElement} root */
  function initVariantSelect(root) {
    const select = root.querySelector('[data-product-variant-select]');
    const hidden = root.querySelector('[data-product-variant-id]');
    const label = root.querySelector('[data-product-variant-label]');
    if (!(select instanceof HTMLSelectElement) || !(hidden instanceof HTMLInputElement)) return;

    const variants = readVariantRecords(root);

    /** @param {string} value */
    const sync = (value) => {
      hidden.value = value;
      hidden.dispatchEvent(new Event('change', { bubbles: true }));
      if (label instanceof HTMLElement) {
        const option = select.selectedOptions[0];
        label.textContent = option ? formatVariantLabel(option.textContent?.trim() || '') : '';
      }

      const variant = variants.find((entry) => String(entry?.id) === String(value));
      if (variant) applyVariantState(root, variant);
    };

    sync(select.value);

    select.addEventListener('change', () => {
      sync(select.value);
    });
  }

  /** @param {number} slideIndex @param {NodeListOf<Element>} slides */
  function isMagic360Slide(slideIndex, slides) {
    const slide = slides[slideIndex];
    return slide instanceof HTMLElement && slide.hasAttribute('data-magic360-slide');
  }

  /** @param {() => void} callback */
  function whenMagic360Ready(callback) {
    const root = document.querySelector('[data-magic360-root]');
    const started = root instanceof HTMLElement && root.dataset.magic360Ready === 'true';
    if (window.Magic360 && started) {
      callback();
      return;
    }
    document.dispatchEvent(new CustomEvent('magic360:load'));
    document.addEventListener('magic360:ready', callback, { once: true });
  }

  /** @param {HTMLElement} root */
  function initMedia(root) {
    const slides = root.querySelectorAll('[data-product-media-slide]');
    const stage = root.querySelector('[data-product-media-stage]');
    if (!slides.length) return;

    const thumbs = root.querySelectorAll('[data-product-media-thumb]');
    const prevBtn = root.querySelector('[data-product-media-prev]');
    const nextBtn = root.querySelector('[data-product-media-next]');

    /** @type {number} */
    let index = 0;

    /** @param {number} next */
    function show(next) {
      if (slides.length <= 1) return;
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        if (!(slide instanceof HTMLElement)) return;
        const active = i === index;
        slide.hidden = !active;
        if (active) return;
        const video = slide.querySelector('video');
        if (video instanceof HTMLVideoElement && !video.paused) video.pause();
      });
      thumbs.forEach((thumb) => {
        if (!(thumb instanceof HTMLElement)) return;
        const active = Number.parseInt(thumb.dataset.index || '-1', 10) === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    root.addEventListener('map:product-show-media', (event) => {
      const mediaId = event instanceof CustomEvent ? String(event.detail?.mediaId || '') : '';
      if (!mediaId) {
        show(0);
        return;
      }
      const target = [...slides].findIndex(
        (slide) => slide instanceof HTMLElement && slide.dataset.mediaId === mediaId,
      );
      if (target >= 0) show(target);
    });

    /** @param {number} delta */
    function step(delta) {
      show(index + delta);
    }

    root.querySelector('[data-product-media-play]')?.addEventListener('click', () => {
      if (isMagic360Slide(index, slides)) {
        // Spinning state lives on the slide element, not in this closure:
        // section reloads replace the DOM (and the Magic360 instance with it),
        // so the state resets in lockstep with the viewer it describes.
        const slide = slides[index];
        whenMagic360Ready(() => {
          if (!window.Magic360 || !(slide instanceof HTMLElement)) return;
          const spinning = slide.dataset.magic360Spinning === 'true';
          if (spinning && typeof window.Magic360.stop === 'function') {
            window.Magic360.stop('magic-spin-a');
            slide.dataset.magic360Spinning = 'false';
            return;
          }
          if (typeof window.Magic360.spin === 'function') {
            window.Magic360.spin('magic-spin-a');
            slide.dataset.magic360Spinning = 'true';
          }
        });
        return;
      }

      const activeSlide = slides[index];
      const video = activeSlide instanceof HTMLElement ? activeSlide.querySelector('video') : null;
      if (video instanceof HTMLVideoElement) {
        video.play();
        return;
      }
      step(1);
    });

    const lightbox = root.querySelector('[data-product-media-lightbox]');
    const lightboxImage = root.querySelector('[data-product-media-lightbox-image]');
    const lightboxVideo = root.querySelector('[data-product-media-lightbox-video]');
    const lightboxClose = root.querySelector('[data-product-media-lightbox-close]');
    const lightboxPrev = root.querySelector('[data-product-media-lightbox-prev]');
    const lightboxNext = root.querySelector('[data-product-media-lightbox-next]');
    /** @type {number} */
    let lightboxIndex = 0;

    /** @param {Element} slide */
    function slideMedia(slide) {
      if (!(slide instanceof HTMLElement)) return { type: 'empty', src: '', alt: '' };
      if (slide.hasAttribute('data-magic360-slide')) {
        const img = slide.querySelector('img');
        return {
          type: 'spin',
          src: slide.dataset.fullSrc || (img instanceof HTMLImageElement ? img.currentSrc || img.src : ''),
          alt: img instanceof HTMLImageElement ? img.alt : '',
        };
      }
      const video = slide.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        return { type: 'video', src: '', alt: '', video };
      }
      const image = slide.querySelector('img');
      return {
        type: 'image',
        src: slide.dataset.fullSrc || (image instanceof HTMLImageElement ? image.currentSrc || image.src : ''),
        alt: image instanceof HTMLImageElement ? image.alt : '',
      };
    }

    /** @param {number} next */
    function renderLightbox(next) {
      if (!(lightbox instanceof HTMLDialogElement)) return;
      lightboxIndex = (next + slides.length) % slides.length;
      const media = slideMedia(slides[lightboxIndex]);
      if (lightboxImage instanceof HTMLImageElement) {
        if (media.type === 'image' || media.type === 'spin') {
          lightboxImage.hidden = false;
          lightboxImage.src = media.src;
          lightboxImage.alt = media.alt || '';
        } else {
          lightboxImage.hidden = true;
          lightboxImage.removeAttribute('src');
        }
      }
      if (lightboxVideo instanceof HTMLElement) {
        lightboxVideo.replaceChildren();
        if (media.type === 'video' && media.video instanceof HTMLVideoElement) {
          const clone = media.video.cloneNode(true);
          if (clone instanceof HTMLVideoElement) {
            clone.controls = true;
            clone.autoplay = true;
            lightboxVideo.append(clone);
          }
          lightboxVideo.hidden = false;
        } else {
          lightboxVideo.hidden = true;
        }
      }
      const many = slides.length > 1;
      if (lightboxPrev instanceof HTMLElement) lightboxPrev.hidden = !many;
      if (lightboxNext instanceof HTMLElement) lightboxNext.hidden = !many;
    }

    function closeLightbox() {
      if (lightbox instanceof HTMLDialogElement && lightbox.open) lightbox.close();
      if (lightboxVideo instanceof HTMLElement) lightboxVideo.replaceChildren();
    }

    function openLightbox(startIndex) {
      if (!(lightbox instanceof HTMLDialogElement)) return;
      renderLightbox(startIndex);
      if (!lightbox.open) lightbox.showModal();
      if (lightboxClose instanceof HTMLElement) lightboxClose.focus();
    }

    /** @returns {boolean} */
    function tryMagic360Fullscreen() {
      const nativeButton = root.querySelector('button.m360-icon-fullscreen-open');
      if (nativeButton instanceof HTMLButtonElement) {
        nativeButton.click();
        return true;
      }
      if (typeof window.Magic360?.fullscreen === 'function') {
        window.Magic360.fullscreen('magic-spin-a');
        return true;
      }
      return false;
    }

    root.querySelector('[data-product-media-expand]')?.addEventListener('click', () => {
      if (isMagic360Slide(index, slides)) {
        whenMagic360Ready(() => {
          if (!tryMagic360Fullscreen()) {
            openLightbox(index);
            return;
          }
          window.setTimeout(() => {
            const el = document.fullscreenElement;
            if (!(el instanceof Element)) {
              openLightbox(index);
              return;
            }
            const box = el.getBoundingClientRect();
            const fills =
              box.width >= window.innerWidth * 0.7 && box.height >= window.innerHeight * 0.7;
            if (!fills) {
              document.exitFullscreen?.().catch(() => {});
              openLightbox(index);
            }
          }, 250);
        });
        return;
      }
      openLightbox(index);
    });

    if (lightbox instanceof HTMLDialogElement) {
      lightboxClose?.addEventListener('click', () => closeLightbox());
      lightboxPrev?.addEventListener('click', (event) => {
        event.stopPropagation();
        renderLightbox(lightboxIndex - 1);
      });
      lightboxNext?.addEventListener('click', (event) => {
        event.stopPropagation();
        renderLightbox(lightboxIndex + 1);
      });
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox || event.target === lightbox.querySelector('.product-media-lightbox__stage')) {
          closeLightbox();
        }
      });
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') renderLightbox(lightboxIndex - 1);
        if (event.key === 'ArrowRight') renderLightbox(lightboxIndex + 1);
      });
      lightbox.addEventListener('close', () => {
        if (lightboxVideo instanceof HTMLElement) lightboxVideo.replaceChildren();
      });
    }

    prevBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      step(-1);
    });
    nextBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      step(1);
    });
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!(thumb instanceof HTMLElement)) return;
        const next = Number.parseInt(thumb.dataset.index || '0', 10);
        if (Number.isFinite(next)) show(next);
      });
    });

    if (slides.length > 1 && stage instanceof HTMLElement) {
      stage.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          step(1);
        }
      });

      /** @type {number | null} */
      let pointerStartX = null;
      /** @type {number | null} */
      let pointerStartY = null;
      let pointerOnSpin = false;

      stage.addEventListener('pointerdown', (event) => {
        if (!(event.target instanceof Element)) return;
        if (
          event.target.closest(
            '[data-product-media-prev], [data-product-media-next], [data-product-media-expand]',
          )
        ) {
          return;
        }
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        pointerOnSpin = Boolean(event.target.closest('[data-magic360-slide]'));
      });

      stage.addEventListener('pointerup', (event) => {
        if (pointerStartX == null || pointerOnSpin) {
          pointerStartX = null;
          pointerStartY = null;
          pointerOnSpin = false;
          return;
        }
        const dx = event.clientX - pointerStartX;
        const dy = event.clientY - (pointerStartY ?? event.clientY);
        pointerStartX = null;
        pointerStartY = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        step(dx < 0 ? 1 : -1);
      });

      stage.addEventListener('pointercancel', () => {
        pointerStartX = null;
        pointerStartY = null;
        pointerOnSpin = false;
      });
    }
  }

  /** @param {HTMLElement} root */
  function initWallet(root) {
    root.querySelectorAll('[data-product-wallet]').forEach((wallet) => {
      if (!(wallet instanceof HTMLElement) || wallet.dataset.productWalletInit === 'true') return;
      wallet.dataset.productWalletInit = 'true';

      /** @param {HTMLElement} container */
      const findNativeControl = (container) =>
        container.querySelector(
          '.shopify-payment-button__button, shop-pay-button, shopify-accelerated-checkout'
        );

      /** @param {HTMLElement} container */
      const sync = (container) => {
        const native = findNativeControl(container);
        wallet.classList.toggle('is-empty', !native);
      };

      const nativeWrap = wallet.querySelector('.product-page__wallet-native');
      if (!(nativeWrap instanceof HTMLElement)) return;

      sync(nativeWrap);

      wallet.addEventListener('click', () => {
        triggerNativeCheckout(nativeWrap);
      });

      wallet.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        triggerNativeCheckout(nativeWrap);
      });

      const observer = new MutationObserver(() => sync(nativeWrap));
      observer.observe(nativeWrap, { childList: true, subtree: true });
    });
  }

  /** @param {HTMLElement} nativeWrap */
  function triggerNativeCheckout(nativeWrap) {
    const native = nativeWrap.querySelector(
      '.shopify-payment-button__button, shop-pay-button, shopify-accelerated-checkout'
    );
    if (native instanceof HTMLElement) {
      native.click();
    }
  }

  /** @param {HTMLElement} root */
  function initFitmentStatus(root) {
    const pill = root.querySelector('[data-product-fitment-pill]');
    const compatibilityBtn = root.querySelector('.product-page__compatibility');
    const compatibilityWrap = root.querySelector('.product-page__compatibility-wrap');
    if (!(pill instanceof HTMLElement)) return;

    const fitment = window.MAPFitment;
    if (!fitment) return;

    /** @type {{ fits: string; misfit: string; change: string; findPart: string }} */
    const copy = {
      fits: root.dataset.productFitmentFits || 'Fits {{ vehicle }}',
      misfit: root.dataset.productFitmentMisfit || 'Does not fit {{ vehicle }}',
      change: root.dataset.productFitmentChange || 'Change',
      findPart: root.dataset.productFitmentFind || 'Find compatible part',
    };

    function readEquivalents() {
      const script = root.querySelector('[data-product-equivalents]');
      if (!(script instanceof HTMLScriptElement)) return [];
      try {
        const parsed = JSON.parse(script.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    /**
     * Collection to shop when this product does not fit. Liquid already
     * prefers a part category over a vehicle catalogue. *-spare-parts is one
     * model only, so a leftover vehicle handle is dropped — tagging Ford
     * Taurus with a Falcon filter returns an empty grid.
     */
    function shopCollectionHandle() {
      const handle = (root.dataset.productCollectionHandle || 'all').trim();
      if (!handle || /-spare-parts$/i.test(handle)) return 'all';
      return handle;
    }

    /**
     * Contract §2 URL scoped to this product's collection — the path builder
     * itself lives once in map-utils.js (WIL-461).
     * @param {Record<string, string | undefined> | null} vehicle
     */
    function vehicleShopUrl(vehicle) {
      const handle = shopCollectionHandle();
      if (!vehicle?.make) return `/collections/${handle}`;
      return window.MapUtils.vehicleShopUrl(vehicle).replace(/^\/collections\/all\b/, `/collections/${handle}`);
    }

    /** @param {Record<string, string | undefined> | null} vehicle */
    function compatiblePartUrl(vehicle) {
      const match = readEquivalents().find((item) => {
        if (!item || typeof item !== 'object') return false;
        return (
          fitment.matchProductFitment(vehicle, {
            tags: Array.isArray(item.tags) ? item.tags : [],
            compatibleVehicles: Array.isArray(item.compatibleVehicles) ? item.compatibleVehicles : [],
          }) === true
        );
      });
      if (match && typeof match.url === 'string' && match.url) return match.url;
      return vehicleShopUrl(vehicle);
    }

    /** @param {string} drawerId */
    function openDrawer(drawerId) {
      const existing = document.querySelector(`[data-map-drawer-open="${drawerId}"]`);
      if (existing instanceof HTMLElement) {
        existing.click();
      }
    }

    function readProductFitment() {
      let tags = [];
      let compatibleVehicles = [];
      try {
        tags = JSON.parse(root.dataset.productFitmentTags || '[]');
      } catch {
        tags = [];
      }
      try {
        compatibleVehicles = JSON.parse(root.dataset.productFitmentVehicles || '[]');
      } catch {
        compatibleVehicles = [];
      }
      return { tags, compatibleVehicles };
    }

    function render() {
      const vehicle = fitment.readSelectedVehicle();
      const match = fitment.matchProductFitment(vehicle, readProductFitment());
      const label = fitment.vehicleLabel(vehicle);

      if (match === null || !label) {
        pill.hidden = true;
        pill.replaceChildren();
        if (compatibilityBtn instanceof HTMLElement) compatibilityBtn.hidden = false;
        if (compatibilityWrap instanceof HTMLElement) compatibilityWrap.hidden = false;
        return;
      }

      if (compatibilityBtn instanceof HTMLElement) compatibilityBtn.hidden = true;
      if (compatibilityWrap instanceof HTMLElement) compatibilityWrap.hidden = true;

      pill.hidden = false;
      pill.classList.remove('product-page__fitment-pill--fits', 'product-page__fitment-pill--misfit');

      /** @param {'tick' | 'cross'} kind */
      const buildIcon = (kind) => {
        const icon = document.createElement('span');
        icon.className = 'product-page__fitment-pill-icon';
        icon.setAttribute('aria-hidden', 'true');
        const template = document.getElementById(
          kind === 'cross' ? 'MapFitmentIconCross' : 'MapFitmentIconTick'
        );
        if (template instanceof HTMLTemplateElement) {
          icon.append(template.content.cloneNode(true));
        }
        return icon;
      };

      /** @param {string} text */
      const buildText = (text) => {
        const span = document.createElement('span');
        span.className = 'product-page__fitment-pill-text';
        span.textContent = text;
        return span;
      };

      pill.replaceChildren();

      if (match) {
        pill.classList.add('product-page__fitment-pill--fits');
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'product-page__fitment-pill-action';
        action.textContent = copy.change;
        action.addEventListener('click', () => {
          openDrawer('HeaderVehicleDrawer');
        });
        pill.append(buildIcon('tick'), buildText(fitment.formatCopy(copy.fits, label)), action);
      } else {
        pill.classList.add('product-page__fitment-pill--misfit');
        const action = document.createElement('a');
        action.className = 'product-page__fitment-pill-action';
        action.href = compatiblePartUrl(vehicle);
        action.textContent = copy.findPart;
        pill.append(buildIcon('cross'), buildText(fitment.formatCopy(copy.misfit, label)), action);
      }
    }

    render();
    window.addEventListener('map:vehicle-selected', render);
    window.addEventListener('storage', (event) => {
      if (event.key === 'selectedVehicle') render();
    });
  }

  /** @param {HTMLElement} root */
  function initCompatibleVehiclesAccordion(root) {
    root.querySelectorAll('[data-product-compatible-vehicles]:not([data-product-compatible-vehicles-init])').forEach((block) => {
      if (!(block instanceof HTMLElement)) return;
      block.dataset.productCompatibleVehiclesInit = 'true';

      block.querySelectorAll('.accordion').forEach((heading) => {
        if (!(heading instanceof HTMLElement)) return;

        const panel = heading.nextElementSibling;
        if (!(panel instanceof HTMLElement) || !panel.classList.contains('panel')) return;

        if (!heading.hasAttribute('tabindex')) heading.tabIndex = 0;
        if (!heading.hasAttribute('role')) heading.setAttribute('role', 'button');
        heading.setAttribute('aria-expanded', heading.classList.contains('active') ? 'true' : 'false');
        panel.hidden = !heading.classList.contains('active');

        /** @param {boolean} open */
        const setOpen = (open) => {
          block.querySelectorAll('.accordion.active').forEach((activeHeading) => {
            if (!(activeHeading instanceof HTMLElement)) return;
            if (activeHeading === heading) return;
            activeHeading.classList.remove('active');
            activeHeading.setAttribute('aria-expanded', 'false');
            const activePanel = activeHeading.nextElementSibling;
            if (activePanel instanceof HTMLElement && activePanel.classList.contains('panel')) {
              activePanel.hidden = true;
            }
          });

          heading.classList.toggle('active', open);
          heading.setAttribute('aria-expanded', open ? 'true' : 'false');
          panel.hidden = !open;
        };

        heading.addEventListener('click', (event) => {
          event.preventDefault();
          setOpen(!heading.classList.contains('active'));
        });

        heading.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          setOpen(!heading.classList.contains('active'));
        });
      });
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {string} wrapSelector
   * @param {string} toggleSelector
   * @param {string} menuSelector
   */
  function initPillMenu(root, wrapSelector, toggleSelector, menuSelector) {
    root.querySelectorAll(wrapSelector).forEach((wrap) => {
      if (!(wrap instanceof HTMLElement) || wrap.dataset.pillMenuInit === 'true') return;
      wrap.dataset.pillMenuInit = 'true';

      const toggle = wrap.querySelector(toggleSelector);
      const menu = wrap.querySelector(menuSelector);
      if (!(toggle instanceof HTMLButtonElement) || !(menu instanceof HTMLElement)) return;

      const setOpen = (open) => {
        wrap.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        menu.hidden = !open;
      };

      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        if (wrap.classList.contains('is-single-option')) return;
        setOpen(menu.hidden);
      });

      wrap.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element) || !target.closest('[data-map-modal-open]')) return;
        setOpen(false);
      });

      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Node) || wrap.contains(event.target)) return;
        setOpen(false);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') setOpen(false);
      });
    });
  }

  /**
   * @param {string} raw
   * @returns {string}
   */
  function classificationKeyFromRaw(raw) {
    const value = String(raw || '')
      .trim()
      .toLowerCase();
    if (value === 'genuine' || value === 'oem' || value === 'oem part') return 'oem';
    if (value === 'aftermarket' || value === 'aftermarket part' || value === 'after market') return 'aftermarket';
    if (value === 'oes' || value === 'oes part') return 'oes';
    if (value === 'q-part certified' || value === 'certified' || value === 'certified part') return 'certified';
    return '';
  }

  /**
   * @param {string[]} tags
   * @returns {string}
   */
  function classificationRawFromTags(tags) {
    if (!Array.isArray(tags)) return '';
    for (const tag of tags) {
      if (typeof tag === 'string' && tag.includes('Part Classification:')) {
        return tag.split('Part Classification:')[1].trim();
      }
    }
    return '';
  }

  /**
   * @param {{ tags?: string[]; title?: string; handle?: string }} item
   * @returns {string}
   */
  function detectSideLabel(item) {
    const tags = Array.isArray(item?.tags) ? item.tags : [];
    const named = [];
    tags.forEach((tag) => {
      if (typeof tag !== 'string' || !tag.startsWith('side_')) return;
      const side = tag.replace('side_', '').trim();
      if (side) named.push(side);
    });
    if (named.length > 1) return 'Driver + Passenger side';
    if (named.length === 1) {
      return /side$/i.test(named[0]) ? named[0] : `${named[0]} side`;
    }

    const text = `${item?.title || ''} ${String(item?.handle || '').replace(/-/g, ' ')}`;
    if (/passenger\s+side/i.test(text)) return 'Passenger side';
    if (/drivers?\s+side/i.test(text)) return 'Driver side';
    if (/both\s+sides|driver\s*\+\s*passenger|lhs\s+and\s+rhs/i.test(text)) return 'Driver + Passenger side';
    return '';
  }

  /**
   * @param {string} title
   * @param {string} currentSide
   * @returns {string[]}
   */
  function alternateSideQueries(title, currentSide) {
    const source = String(title || '').trim();
    if (!source) return [];
    const side = String(currentSide || '').toLowerCase();
    const queries = [];
    if (side.includes('passenger') && !side.includes('driver')) {
      queries.push(source.replace(/passenger\s+side/gi, 'Drivers Side'));
    } else if (side.includes('driver') && !side.includes('passenger')) {
      queries.push(source.replace(/drivers?\s+side/gi, 'Passenger Side'));
    } else if (side.includes('driver') && side.includes('passenger')) {
      queries.push(`${source} Drivers Side`, `${source} Passenger Side`);
    }
    return queries.filter((query, index, list) => query && query !== source && list.indexOf(query) === index);
  }

  /** @param {string} handle */
  function swapHandleSide(handle) {
    const value = String(handle || '');
    if (value.includes('drivers-side')) return value.replace('drivers-side', 'passenger-side');
    if (value.includes('driver-side')) return value.replace('driver-side', 'passenger-side');
    if (value.includes('passenger-side')) return value.replace('passenger-side', 'drivers-side');
    return '';
  }

  /**
   * Opposite-hand code for a parent item or APG part.
   * GVE-21041RH → GVE-21041LH, GVE-21041RHQ → GVE-21041LHQ.
   * @param {string} value
   * @returns {string}
   */
  function siblingSideCode(value) {
    const text = String(value || '').trim();
    const match = text.match(/^(.*)(RH|LH)(Q)?$/i);
    if (!match) return '';
    const opposite = match[2].toUpperCase() === 'RH' ? 'LH' : 'RH';
    const suffix = match[3] ? 'Q' : '';
    const swapped = `${match[1]}${opposite}${suffix}`;
    return swapped.toLowerCase() === text.toLowerCase() ? '' : swapped;
  }

  /**
   * @param {string[]} tags
   * @param {string} key parentitem | apgpartno
   * @returns {string[]}
   */
  function tagValues(tags, key) {
    const values = [];
    const equals = `${key}=`;
    const underscore = `${key}_`;
    tags.forEach((tag) => {
      if (typeof tag !== 'string') return;
      const lower = tag.toLowerCase();
      if (lower.startsWith(equals)) values.push(tag.slice(equals.length).trim());
      else if (lower.startsWith(underscore)) values.push(tag.slice(underscore.length).trim());
    });
    return values;
  }

  /** @param {string} value */
  function normalizeSideTitle(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /**
   * Why this product is the opposite side of the current SKU.
   * A shared phrase ("Passenger Side Certified suits Holden") is not enough —
   * that was linking the VE tail lamp to a VF lamp or a VE door mirror.
   * @param {unknown} item
   * @param {{ siblingParent: string, siblingPart: string, swappedHandle: string, swappedTitle: string }} ctx
   * @returns {'parent' | 'part' | 'handle' | 'title' | ''}
   */
  function sideSiblingIdentity(item, ctx) {
    const tags = productTags(item);
    const parents = tagValues(tags, 'parentitem').map((value) => value.toLowerCase());
    const parts = tagValues(tags, 'apgpartno').map((value) => value.toLowerCase());
    const wantedParent = ctx.siblingParent.toLowerCase();
    const wantedPart = ctx.siblingPart.toLowerCase();
    if (wantedParent && parents.includes(wantedParent)) return 'parent';
    if (wantedPart && parts.includes(wantedPart)) return 'part';

    const parentConflicts = Boolean(wantedParent && parents.length && !parents.includes(wantedParent));
    if (parentConflicts) return '';

    if (ctx.swappedHandle && item?.handle === ctx.swappedHandle) return 'handle';
    const wantedTitle = normalizeSideTitle(ctx.swappedTitle);
    if (wantedTitle && normalizeSideTitle(item?.title) === wantedTitle) return 'title';
    return '';
  }

  /** @param {unknown} item */
  function productTags(item) {
    const tags = item?.tags;
    if (Array.isArray(tags)) return tags;
    if (typeof tags === 'string') return tags.split(',').map((tag) => tag.trim());
    return [];
  }

  let crossSellAbort = null;

  /**
   * One section render of the vehicle collection (WIL-538).
   * https://shopify.dev/docs/api/ajax/section-rendering
   * @param {{ make: string, model: string, series: string }} vehicle
   */
  function crossSellSectionUrl(vehicle) {
    const shopUrl = window.MapUtils?.vehicleShopUrl
      ? window.MapUtils.vehicleShopUrl({
          make: vehicle.make,
          model: vehicle.model,
          series: vehicle.series,
        })
      : '/collections/all';
    const url = new URL(shopUrl, window.location.origin);
    url.searchParams.set('section_id', 'cross-sell-results');
    return `${url.pathname}${url.search}`;
  }

  /**
   * @param {Element[]} nodes
   * @param {Set<string>} used
   * @param {number} limit
   * @param {(node: Element) => boolean} [predicate]
   */
  function takeCrossSellNodes(nodes, used, limit, predicate) {
    const picked = [];
    nodes.forEach((node) => {
      if (picked.length >= limit) return;
      const handle = node.getAttribute('data-cross-sell-handle') || '';
      if (!handle || used.has(handle)) return;
      if (predicate && !predicate(node)) return;
      used.add(handle);
      picked.push(node);
    });
    return picked;
  }

  /**
   * @param {Element} source
   * @param {'accessories' | 'related'} kind
   */
  function crossSellListItem(source, kind) {
    const template = source.querySelector(
      kind === 'accessories' ? '[data-cross-sell-accessory]' : '[data-cross-sell-related]',
    );
    if (!(template instanceof HTMLTemplateElement)) return null;
    const li = document.createElement('li');
    li.appendChild(template.content.cloneNode(true));
    return li;
  }

  /**
   * @param {HTMLElement | undefined} mount
   * @param {Element[]} nodes
   * @param {'accessories' | 'related'} kind
   */
  function fillCrossSell(mount, nodes, kind) {
    if (!(mount instanceof HTMLElement)) return;
    const list = mount.querySelector('[data-cross-sell-list]');
    if (!(list instanceof HTMLElement)) return;
    const cards = nodes.map((node) => crossSellListItem(node, kind)).filter(Boolean);
    if (!cards.length) {
      mount.hidden = true;
      return;
    }
    list.replaceChildren();
    cards.forEach((card) => list.appendChild(card));
    mount.hidden = false;
  }

  /** @param {HTMLElement} root */
  function initCrossSellSearch(root) {
    const mounts = Array.from(root.querySelectorAll('[data-cross-sell-search]')).filter(
      (node) => node instanceof HTMLElement,
    );
    if (!mounts.length) return;

    const accessories = mounts.find((node) => node.dataset.crossSellSearch === 'accessories');
    const related = mounts.find((node) => node.dataset.crossSellSearch === 'related');
    const source = accessories || related;
    if (!source) return;

    const vehicle = {
      make: source.dataset.crossSellMake || '',
      model: source.dataset.crossSellModel || '',
      series: source.dataset.crossSellSeries || '',
    };
    const category = source.dataset.crossSellCategory || '';
    const currentHandle = source.dataset.crossSellHandle || '';
    if (!vehicle.make || !vehicle.model) return;

    if (crossSellAbort) crossSellAbort.abort();
    const controller = new AbortController();
    crossSellAbort = controller;
    if (!initCrossSellSearch.bound) {
      window.addEventListener('pagehide', () => crossSellAbort?.abort());
      initCrossSellSearch.bound = true;
    }

    fetch(crossSellSectionUrl(vehicle), { signal: controller.signal })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (controller.signal.aborted) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nodes = Array.from(doc.querySelectorAll('[data-cross-sell-handle]'));
        const used = new Set([currentHandle]);
        const accessoryLimit = Number.parseInt(accessories?.dataset.crossSellLimit || '3', 10) || 3;
        const relatedLimit = Number.parseInt(related?.dataset.crossSellLimit || '4', 10) || 4;
        const categoryOf = (node) => node.getAttribute('data-cross-sell-category') || '';
        const familyOf = (node) => node.getAttribute('data-cross-sell-family') || categoryOf(node);

        let accessoryNodes = [];
        if (accessories) {
          const seenCategories = new Set();
          accessoryNodes = takeCrossSellNodes(nodes, used, accessoryLimit, (node) => {
            const itemCategory = categoryOf(node);
            if (category && itemCategory === category) return false;
            if (itemCategory && seenCategories.has(itemCategory)) return false;
            if (itemCategory) seenCategories.add(itemCategory);
            return true;
          });
          if (accessoryNodes.length < accessoryLimit) {
            accessoryNodes = accessoryNodes.concat(
              takeCrossSellNodes(nodes, used, accessoryLimit - accessoryNodes.length, (node) => {
                return !category || categoryOf(node) !== category;
              }),
            );
          }
          if (accessoryNodes.length < accessoryLimit) {
            accessoryNodes = accessoryNodes.concat(
              takeCrossSellNodes(nodes, used, accessoryLimit - accessoryNodes.length),
            );
          }
        }

        let relatedNodes = [];
        if (related) {
          const currentFamily = related.dataset.crossSellFamily || category;
          const seenFamilies = new Set(accessoryNodes.map((node) => familyOf(node)).filter(Boolean));
          if (currentFamily) seenFamilies.add(currentFamily);
          relatedNodes = takeCrossSellNodes(nodes, used, relatedLimit, (node) => {
            const family = familyOf(node);
            if (!family || seenFamilies.has(family)) return false;
            seenFamilies.add(family);
            return true;
          });
        }

        fillCrossSell(accessories, accessoryNodes, 'accessories');
        fillCrossSell(related, relatedNodes, 'related');
        window.MAPProductCard?.init?.();
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        mounts.forEach((mount) => {
          mount.hidden = true;
        });
      });
  }

  /** @param {HTMLElement} menu */
  function finalizePillMenu(menu) {
    const wrap = menu.closest('[data-product-classification], [data-product-side]');
    if (!(wrap instanceof HTMLElement)) return;
    const hasLinks = Boolean(menu.querySelector('a.product-page__pill-option'));
    wrap.classList.toggle('is-single-option', !hasLinks);
    const toggle = wrap.querySelector('[data-product-classification-toggle], [data-product-side-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;
    if (!hasLinks) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-disabled', 'true');
      menu.hidden = true;
    } else {
      toggle.removeAttribute('aria-disabled');
    }
  }

  /** @param {HTMLElement} menu */
  function hydrateParentItemOptions(menu) {
    const parentItem = menu.dataset.parentItem;
    const currentHandle = menu.dataset.productHandle || '';
    const currentKey = menu.dataset.currentClassification || '';
    if (!parentItem) {
      finalizePillMenu(menu);
      return;
    }

    const labels = {
      oem: menu.dataset.labelOem || 'OEM Part',
      aftermarket: menu.dataset.labelAftermarket || 'Aftermarket',
      oes: menu.dataset.labelOes || 'OES',
      certified: menu.dataset.labelCertified || 'Certified',
    };
    const availableLabel = menu.dataset.availableLabel || 'Available';
    const currentTitle = menu.dataset.productTitle || '';
    const currentIsCover = /cover/i.test(currentTitle);
    const currentSide = detectSideLabel({ handle: currentHandle, title: currentTitle });
    const seen = new Set(
      [currentKey]
        .concat(
          Array.from(menu.querySelectorAll('a.product-page__pill-option')).map((el) => {
            const attr = el.getAttribute('data-classification-key');
            if (attr) return attr;
            const raw = String(el.textContent || '')
              .split('–')[0]
              .split('—')[0]
              .trim();
            return classificationKeyFromRaw(raw);
          }),
        )
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    );

    const params = new URLSearchParams();
    params.set('q', `parentitem_${parentItem}`);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');

    fetch(`/search/suggest.json?${params.toString()}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const products = payload?.resources?.results?.products;
        if (!Array.isArray(products)) return;

        const ranked = products
          .map((item) => {
            if (!item || item.handle === currentHandle) return null;
            if (!currentIsCover && /cover/i.test(item.title || '')) return null;
            const key = classificationKeyFromRaw(classificationRawFromTags(productTags(item)));
            if (!key || seen.has(key)) return null;
            const itemSide = detectSideLabel({ ...item, tags: productTags(item) });
            let score = 1;
            if (currentSide && itemSide === currentSide) score += 20;
            return { item, key, score };
          })
          .filter(Boolean)
          .sort((a, b) => b.score - a.score);

        ranked.forEach(({ item, key }) => {
          if (seen.has(key)) return;
          seen.add(key);

          const href = typeof item.url === 'string' ? item.url.split('?')[0] : `/products/${item.handle}`;
          const label = labels[key] || key;
          const suffix = item.available === false ? '' : ` – ${availableLabel}`;
          const link = document.createElement('a');
          link.className = 'product-page__pill-option';
          link.href = href;
          link.dataset.classificationKey = key;
          link.setAttribute('aria-label', `${label}${suffix}`);
          link.textContent = `${label}${suffix}`;
          menu.appendChild(link);
        });
      })
      .catch(() => {})
      .finally(() => finalizePillMenu(menu));
  }

  /** @param {HTMLElement} menu */
  function hydrateParentItemSides(menu) {
    const currentHandle = menu.dataset.productHandle || '';
    const currentTitle = menu.dataset.productTitle || '';
    const currentSide = menu.dataset.currentSide || '';
    const parentItem = menu.dataset.parentItem || '';
    const apgPart = menu.dataset.apgPart || '';
    const currentClass = menu.dataset.currentClassification || '';
    const currentIsCover = /cover/i.test(currentTitle);
    const seen = new Set(
      [currentSide, ...Array.from(menu.querySelectorAll('a.product-page__pill-option')).map((el) => el.textContent || '')]
        .map((value) => String(value).trim().toLowerCase())
        .filter(Boolean),
    );
    const siblingParent = siblingSideCode(parentItem);
    const siblingPart = siblingSideCode(apgPart);
    const swappedHandle = swapHandleSide(currentHandle);
    const swappedTitle = alternateSideQueries(currentTitle, currentSide)[0] || '';
    const ctx = { siblingParent, siblingPart, swappedHandle, swappedTitle };
    /** @type {{ item: unknown, score: number, label: string, key: string }[]} */
    const candidates = [];

    /** @param {unknown} item */
    const considerSideProduct = (item) => {
      if (!item || item.handle === currentHandle) return;
      if (!currentIsCover && /cover/i.test(item.title || '')) return;
      const identity = sideSiblingIdentity(item, ctx);
      if (!identity) return;
      const label = detectSideLabel({ ...item, tags: productTags(item) });
      const key = label.trim().toLowerCase();
      if (!key || seen.has(key) || key === currentSide.trim().toLowerCase()) return;
      const rank = { parent: 100, part: 90, handle: 80, title: 40 };
      let score = rank[identity] || 0;
      const itemClass = classificationKeyFromRaw(classificationRawFromTags(productTags(item)));
      if (currentClass && itemClass === currentClass) score += 5;
      candidates.push({ item, score, label, key });
    };

    const pending = [];

    if (swappedHandle) {
      pending.push(
        fetch(`/products/${swappedHandle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .then((product) => {
            if (product) considerSideProduct(product);
          })
          .catch(() => {}),
      );
    }

    const queries = [];
    if (siblingParent) queries.push(`parentitem_${siblingParent}`);
    if (siblingPart) queries.push(`apgpartno_${siblingPart}`);
    if (swappedTitle) queries.push(swappedTitle);

    queries.forEach((query) => {
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '10');
      params.set('resources[options][unavailable_products]', 'show');
      pending.push(
        fetch(`/search/suggest.json?${params.toString()}`)
          .then((response) => (response.ok ? response.json() : null))
          .then((payload) => {
            const products = payload?.resources?.results?.products;
            if (!Array.isArray(products)) return;
            products.forEach((item) => considerSideProduct(item));
          })
          .catch(() => {}),
      );
    });

    Promise.all(pending).finally(() => {
      candidates.sort((a, b) => b.score - a.score);
      const winner = candidates[0];
      if (winner) {
        seen.add(winner.key);
        const href =
          typeof winner.item.url === 'string' && winner.item.url.startsWith('/')
            ? winner.item.url.split('?')[0]
            : `/products/${winner.item.handle}`;
        const link = document.createElement('a');
        link.className = 'product-page__pill-option';
        link.href = href;
        link.textContent = winner.label;
        menu.appendChild(link);
      }
      finalizePillMenu(menu);
    });
  }

  /** @param {HTMLElement} root */
  function initClassificationMenu(root) {
    initPillMenu(
      root,
      '[data-product-classification]',
      '[data-product-classification-toggle]',
      '[data-product-classification-menu]',
    );
    initPillMenu(root, '[data-product-side]', '[data-product-side-toggle]', '[data-product-side-menu]');

    root.querySelectorAll('[data-product-classification-menu]').forEach((menu) => {
      if (menu instanceof HTMLElement) hydrateParentItemOptions(menu);
    });
    root.querySelectorAll('[data-product-side-menu]').forEach((menu) => {
      if (menu instanceof HTMLElement) hydrateParentItemSides(menu);
    });
  }

  /** @param {HTMLElement} root */
  function initFitmentTrigger(root) {
    const tab = root.querySelector('[data-product-tab="fitment"]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const tabs = root.querySelector('[data-product-tabs]') || tab;
    root.querySelectorAll('[data-product-fitment-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLButtonElement) || trigger.dataset.fitmentTriggerInit === 'true') return;
      trigger.dataset.fitmentTriggerInit = 'true';
      trigger.addEventListener('click', () => {
        tab.click();
        tabs.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /** @param {unknown} value */
  function parseReviewCountValue(value) {
    if (value == null || value === '') return null;
    const count = Number.parseInt(String(value).replace(/[^\d-]/g, ''), 10);
    return Number.isFinite(count) && count >= 0 ? count : null;
  }

  /** @param {ParentNode} root */
  function readLiveReviewCount(root) {
    const scope = root.querySelector('[data-product-reviews]') || root;
    const sources = scope.querySelectorAll(
      '.jdgm-widget[data-number-of-reviews], .jdgm-rev-widg[data-number-of-reviews], .jdgm-prev-badge[data-number-of-reviews], [data-number-of-reviews]',
    );
    for (const el of sources) {
      if (!(el instanceof HTMLElement) || el.hasAttribute('data-review-label-template')) continue;
      const count = parseReviewCountValue(
        el.getAttribute('data-number-of-reviews') || el.dataset.numberOfReviews,
      );
      if (count != null) return count;
    }

    const header = scope.querySelector(
      '.jdgm-rev-widg__summary-text, .jdgm-all-reviews__header, .jdgm-rev-widg__title',
    );
    if (header) {
      const match = String(header.textContent || '').match(/(\d+)\s+reviews?/i);
      if (match) return Number.parseInt(match[1], 10);
    }

    return null;
  }

  /** @param {HTMLElement} root */
  function applyReviewCount(root, count) {
    const next = parseReviewCountValue(count);
    if (next == null) return;

    root.querySelectorAll('[data-review-label-template]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const template = el.dataset.reviewLabelTemplate || '';
      if (!template) return;
      if (el.dataset.reviewCount === String(next) && el.textContent?.includes(`(${next})`)) return;
      el.dataset.reviewCount = String(next);
      el.textContent = template.replace('__COUNT__', String(next));
    });

    const empty = root.querySelector('[data-reviews-empty]');
    if (empty instanceof HTMLElement) {
      empty.hidden = next > 0;
    }
  }

  /** @param {HTMLElement} root */
  function initReviewCount(root) {
    const tab = root.querySelector('[data-product-tab="reviews"]');
    if (!(tab instanceof HTMLElement)) return;

    const sync = () => {
      const live = readLiveReviewCount(root);
      if (live != null) applyReviewCount(root, live);
    };

    sync();

    const reviews = root.querySelector('[data-product-reviews]') || root;
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(sync);
      observer.observe(reviews, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-number-of-reviews', 'data-review-count'],
      });
    }

    ['jdgm:done', 'jdgm:updated', 'jdgm:review-updated', 'jdgm:all-reviews-loaded', 'jdgm:review-added'].forEach(
      (eventName) => {
        document.addEventListener(eventName, sync);
      },
    );
  }

  /** @param {HTMLElement} root */
  function initReviewsTrigger(root) {
    const trigger = root.querySelector('[data-product-reviews-trigger]');
    const tab = root.querySelector('[data-product-tab="reviews"]');
    if (!(trigger instanceof HTMLButtonElement) || !(tab instanceof HTMLButtonElement)) return;

    trigger.addEventListener('click', () => {
      tab.click();
      tab.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /** @param {HTMLElement} root */
  function initStickyCart(root) {
    const purchaseCta = root.querySelector('[data-product-purchase-cta]');
    const sticky = root.querySelector('[data-product-sticky-cart]');
    if (!(purchaseCta instanceof HTMLElement) || !(sticky instanceof HTMLElement)) return;

    let ctaOutOfView = false;
    let footerInView = false;

    const setVisible = (visible) => {
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    const updateSticky = () => {
      setVisible(ctaOutOfView && !footerInView);
    };

    setVisible(false);

    const purchaseForm = root.querySelector('.product-page__purchase-form');
    const mainQty = purchaseForm?.querySelector('.product-card__qty-select[name="quantity"]');
    const stickyQty = sticky.querySelector('[data-product-sticky-qty]');

    if (mainQty instanceof HTMLSelectElement && stickyQty instanceof HTMLSelectElement) {
      const syncStickyDisplay = () => {
        const display = stickyQty.closest('.product-card__qty')?.querySelector('.product-card__qty-display');
        if (display) display.textContent = stickyQty.value;
      };

      stickyQty.addEventListener('change', () => {
        mainQty.value = stickyQty.value;
        mainQty.dispatchEvent(new Event('change', { bubbles: true }));
      });

      mainQty.addEventListener('change', () => {
        stickyQty.value = mainQty.value;
        syncStickyDisplay();
      });
    }

    if (typeof IntersectionObserver === 'undefined') return;

    const ctaObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        ctaOutOfView = !entry.isIntersecting;
        updateSticky();
      },
      { root: null, threshold: 0, rootMargin: '0px 0px -1px 0px' }
    );

    ctaObserver.observe(purchaseCta);

    const footer = document.querySelector('.site-footer');
    if (footer instanceof HTMLElement) {
      const footerObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          footerInView = entry.isIntersecting;
          updateSticky();
        },
        { root: null, threshold: 0, rootMargin: '0px 0px -1px 0px' }
      );

      footerObserver.observe(footer);
    }
  }

  const SHIPPING_STORAGE_KEY = 'mapCartShippingEstimate';

  /** @returns {string} */
  function shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  /** Single source in map-utils.js (WIL-461). @param {string} value */
  function extractPostcode(value) {
    return window.MapUtils.extractPostcode(value);
  }

  /** @returns {Record<string, unknown> | null} */
  function readCollectStore() {
    const store = window.MapClickCollect?.readStoredLocation?.();
    if (store && typeof store === 'object') return store;

    try {
      const parsed = JSON.parse(window.localStorage.getItem('mapCollectLocation') || 'null');
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      /* ignore */
    }

    return null;
  }

  /** @returns {string} */
  function readCollectPostcode() {
    const store = readCollectStore();
    if (!store) return '';
    return extractPostcode(String(store.postcode || store.addressLine || ''));
  }

  /**
   * @param {HTMLElement} row
   * @param {string} postcode
   */
  function formatShippingDisplay(row, postcode) {
    if (!postcode) return '';
    return (
      fillLogisticsTemplate(row.dataset.logisticsDisplay || '', {
        postcode,
        country: row.dataset.logisticsCountry || '',
      }).replace(/(,\s*)+$/g, '').trim() || postcode
    );
  }

  /**
   * @param {{ postcode: string, display: string, shippingText: string } | null} saved
   * @param {HTMLElement} row
   * @returns {{ postcode: string, display: string, shippingText: string } | null}
   */
  function locationFromCollect(saved, row) {
    const collect = readCollectPostcode();
    if (!collect) return null;
    return {
      postcode: collect,
      display: saved?.postcode === collect && saved.display ? saved.display : formatShippingDisplay(row, collect),
      shippingText: saved?.postcode === collect ? saved.shippingText || '' : '',
    };
  }

  /**
   * @param {HTMLElement} row
   * @returns {{ postcode: string, display: string, shippingText: string } | null}
   */
  function knownShippingLocation(row) {
    const saved = readSavedShippingEstimate();
    if (saved?.shippingText && saved.postcode) return saved;
    return locationFromCollect(saved, row) || saved;
  }

  /** @returns {{ postcode: string, display: string, shippingText: string } | null} */
  function readSavedShippingEstimate() {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(SHIPPING_STORAGE_KEY) || 'null');
      if (!parsed || typeof parsed.postcode !== 'string') return null;
      const postcode = extractPostcode(parsed.postcode);
      if (!postcode) return null;
      return {
        postcode,
        display: typeof parsed.display === 'string' ? parsed.display : postcode,
        shippingText: typeof parsed.shippingText === 'string' ? parsed.shippingText : '',
      };
    } catch {
      return null;
    }
  }

  /**
   * @param {{ postcode: string, display: string, shippingText: string } | null} estimate
   */
  function writeSavedShippingEstimate(estimate) {
    try {
      if (!estimate) window.sessionStorage.removeItem(SHIPPING_STORAGE_KEY);
      else window.sessionStorage.setItem(SHIPPING_STORAGE_KEY, JSON.stringify(estimate));
    } catch {
      /* ignore quota / private mode */
    }
    window.dispatchEvent(new CustomEvent('map:shipping-estimate', { detail: estimate }));
  }

  /**
   * @param {HTMLElement} block
   * @param {'' | 'empty' | 'checkout' | 'calculating'} kind
   */
  function setShippingStatus(block, kind) {
    const status = block.querySelector('[data-product-logistics-shipping-status]');
    if (!(status instanceof HTMLElement)) return;
    if (!kind) {
      status.hidden = true;
      status.textContent = '';
      return;
    }

    const messages = {
      empty: status.dataset.shippingEmpty,
      checkout: status.dataset.shippingCheckout,
      calculating: status.dataset.calculating,
    };
    status.textContent = messages[kind] || '';
    status.hidden = !status.textContent;
  }

  /**
   * @param {HTMLElement} block
   * @param {{ postcode: string, display: string, shippingText: string }} location
   */
  function prefillShippingInput(block, location) {
    const input = block.querySelector('[data-product-logistics-shipping-postcode]');
    if (!(input instanceof HTMLInputElement) || !location.postcode) return;
    if (extractPostcode(input.value) === location.postcode) {
      syncShippingSubmit(block);
      return;
    }
    input.value = location.display || location.postcode;
    syncShippingSubmit(block);
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} vars
   */
  function fillLogisticsTemplate(template, vars) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}|\[(\w+)\]/g, (match, braceKey, bracketKey) => {
      const key = braceKey || bracketKey;
      return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match;
    });
  }

  /**
   * @param {HTMLElement} row
   * @returns {{ min: number, max: number }}
   */
  function readDeliveryDays(row) {
    const min = Number.parseInt(row.dataset.logisticsDaysMin || '', 10);
    const max = Number.parseInt(row.dataset.logisticsDaysMax || '', 10);
    if (Number.isFinite(min) && Number.isFinite(max) && min > 0 && max > 0) {
      return { min: Math.min(min, max), max: Math.max(min, max) };
    }
    return { min: 3, max: 5 };
  }

  /** @param {Date} date @param {number} days */
  function addBusinessDays(date, days) {
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let left = Math.max(0, days);
    while (left > 0) {
      next.setDate(next.getDate() + 1);
      const weekday = next.getDay();
      if (weekday !== 0 && weekday !== 6) left -= 1;
    }
    return next;
  }

  /** @param {Date} date */
  function formatArriveDate(date) {
    return new Intl.DateTimeFormat('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
      .format(date)
      .replace(/,/g, '');
  }

  /** @param {HTMLElement} row */
  function shippingDetailVars(row, postcode) {
    const range = readDeliveryDays(row);
    const now = new Date();
    return {
      postcode: postcode || '',
      min: range.min,
      max: range.max,
      from: formatArriveDate(addBusinessDays(now, range.min)),
      to: formatArriveDate(addBusinessDays(now, range.max)),
    };
  }

  /** @param {HTMLElement} row */
  function paintShippingRow(row, postcode, priceText) {
    const title = row.querySelector('[data-product-logistics-title-text]');
    const price = row.querySelector('[data-product-logistics-price]');
    const available = row.querySelector('[data-product-logistics-available]');
    const detail = row.querySelector('[data-product-logistics-detail]');
    const vars = shippingDetailVars(row, postcode);
    const defaultTitle = row.dataset.logisticsDefaultTitle || '';
    const shippingTitle = row.dataset.logisticsShippingTitle || '';
    const estimated = Boolean(postcode);

    row.classList.toggle('is-estimated', estimated);

    if (title instanceof HTMLElement) {
      title.textContent = estimated ? fillLogisticsTemplate(shippingTitle, vars) : defaultTitle;
    }

    if (detail instanceof HTMLElement) {
      detail.textContent = estimated
        ? fillLogisticsTemplate(row.dataset.logisticsArrive || '', vars)
        : fillLogisticsTemplate(row.dataset.logisticsDefaultDetail || '', vars);
    }

    if (price instanceof HTMLElement) {
      if (priceText) {
        price.textContent = priceText;
        price.hidden = false;
      } else {
        price.textContent = '';
        price.hidden = true;
      }
    }

    if (available instanceof HTMLElement) available.hidden = !estimated;
  }

  /** @param {HTMLElement} block */
  function syncShippingSubmit(block) {
    const input = block.querySelector('[data-product-logistics-shipping-postcode]');
    const submit = block.querySelector('[data-product-logistics-shipping-submit]');
    if (!(input instanceof HTMLInputElement) || !(submit instanceof HTMLButtonElement)) return;
    submit.disabled = !extractPostcode(input.value);
  }

  /**
   * @param {HTMLElement} block
   * @param {boolean} open
   */
  function setShippingOpen(block, open) {
    const toggle = block.querySelector('[data-product-logistics-shipping-toggle]');
    const panel = block.querySelector('[data-product-logistics-shipping-panel]');
    block.classList.toggle('is-open', open);
    if (toggle instanceof HTMLButtonElement) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel instanceof HTMLElement) panel.hidden = !open;
    if (open) {
      const input = block.querySelector('[data-product-logistics-shipping-postcode]');
      if (input instanceof HTMLInputElement) input.focus();
    }
  }

  /**
   * @param {HTMLElement} block
   * @param {{ preferCollect?: boolean }} [options]
   */
  async function resolveShippingEstimate(block, options) {
    const saved = readSavedShippingEstimate();
    let location = options?.preferCollect === true
      ? locationFromCollect(saved, block) || knownShippingLocation(block)
      : knownShippingLocation(block);

    if (!location?.postcode) {
      try {
        /* Postcode fallback only — not used to price this product. */
        const response = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
        if (response.ok) {
          const cart = await response.json();
          const postcode = extractPostcode(
            cart?.shipping_address?.zip || cart?.shipping_address?.postal_code || '',
          );
          if (postcode) location = { postcode, display: formatShippingDisplay(block, postcode), shippingText: '' };
        }
      } catch {
        /* ignore */
      }
    }

    if (!location?.postcode) {
      paintShippingRow(block, '', '');
      return;
    }

    prefillShippingInput(block, location);
    setShippingStatus(block, '');
    /* Docs: https://shopify.dev/docs/api/ajax/reference/cart#get-cart-shipping-rates-json
       Prices the current cart, not this product — never paint a cart-based
       rate on PDP (WIL-461). Day window stays; price stays hidden. */
    paintShippingRow(block, location.postcode, '');
  }

  /** @param {HTMLElement} block */
  function estimateShippingFromPanel(block) {
    const input = block.querySelector('[data-product-logistics-shipping-postcode]');
    const submit = block.querySelector('[data-product-logistics-shipping-submit]');
    const postcode = input instanceof HTMLInputElement ? extractPostcode(input.value) : '';

    if (!postcode) {
      setShippingStatus(block, 'empty');
      return;
    }

    if (submit instanceof HTMLButtonElement) submit.disabled = true;

    let display = formatShippingDisplay(block, postcode);
    if (input instanceof HTMLInputElement) {
      if (/^\d{4}$/.test(input.value.trim())) input.value = display;
      else if (input.value.trim()) display = input.value.trim();
    }

    writeSavedShippingEstimate({ postcode, display, shippingText: '' });
    paintShippingRow(block, postcode, '');
    setShippingStatus(block, '');
    setShippingOpen(block, false);
    syncShippingSubmit(block);
  }

  /** @param {HTMLElement} root */
  function initLogisticsShipping(root) {
    root.querySelectorAll('[data-product-logistics-shipping]').forEach((block) => {
      if (!(block instanceof HTMLElement) || block.dataset.logisticsShippingInit === 'true') return;
      block.dataset.logisticsShippingInit = 'true';

      const toggle = block.querySelector('[data-product-logistics-shipping-toggle]');
      const input = block.querySelector('[data-product-logistics-shipping-postcode]');
      const submit = block.querySelector('[data-product-logistics-shipping-submit]');

      toggle?.addEventListener('click', () => {
        const willOpen = !block.classList.contains('is-open');
        if (willOpen) {
          const location = knownShippingLocation(block);
          if (location?.postcode && input instanceof HTMLInputElement && !extractPostcode(input.value)) {
            input.value = location.display || location.postcode;
            syncShippingSubmit(block);
          }
        }
        setShippingOpen(block, willOpen);
      });
      input?.addEventListener('input', () => syncShippingSubmit(block));
      input?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (submit instanceof HTMLButtonElement && !submit.disabled) void estimateShippingFromPanel(block);
      });
      submit?.addEventListener('click', () => {
        void estimateShippingFromPanel(block);
      });

      syncShippingSubmit(block);
      void resolveShippingEstimate(block);
      window.addEventListener('map:shipping-estimate', () => {
        void resolveShippingEstimate(block);
      });
      window.addEventListener('map:collect-location-selected', () => {
        void resolveShippingEstimate(block, { preferCollect: true });
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-product-tabs]:not([data-product-tabs-init])').forEach((root) => {
      if (root instanceof HTMLElement) {
        root.dataset.productTabsInit = 'true';
        initTabs(root);
      }
    });

    document.querySelectorAll('[data-product-page]:not([data-product-page-init])').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      root.dataset.productPageInit = 'true';

      initSuitsLine(root);
      root.querySelectorAll('[data-product-description]').forEach((block) => {
        if (block instanceof HTMLElement) initDescription(block);
      });

      const media = root.querySelector('[data-product-media]');
      if (media instanceof HTMLElement) initMedia(media);

      initVariantSelect(root);
      initWallet(root);
      initFitmentStatus(root);
      initClassificationMenu(root);
      initCrossSellSearch(root);
      initFitmentTrigger(root);
      initReviewsTrigger(root);
      initReviewCount(root);
      initStickyCart(root);
      initCompatibleVehiclesAccordion(root);
      initLogisticsShipping(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();